import MyExpApi from "apps/myExperience/enum/MyExpApi";
import { getApiQueryKey, useGetApiData, usePostApiData } from "shared/datalayer";
import ApiBulkTicketRevoke from "shared/types/ApiBulkTicketRevoke";
import { QueryClient } from "react-query";
import { useCopies } from "shared/hooks/useCopies";
import { successToast } from "shared/utils/toastNotification";
import { ApiBulkTicket } from "./types";
import InventoryType from "shared/enums/InventoryType";
import { ApiBulkTicketEnabledForms } from "shared/types/ApiBulkTicketEnabledForms";
import { InvoicePaymentRequestStatus } from "shared/enums/InvoicePaymentRequestStatus";

export function getBulkTicketDetailsKey(registrationParticipantId?: string) {
  return getApiQueryKey(MyExpApi.BULK_TICKET_DETAIL_FOR_BTA, {
    pathParams: { registrationParticipantId }
  });
}

export function useGetBulkTicketDetails({
  registrationParticipantId,
  errorHandler,
  successHandler
}: {
  registrationParticipantId?: string;
  errorHandler?: () => void;
  successHandler?: (data: Array<ApiBulkTicket>) => void;
}) {
  return useGetApiData<Array<ApiBulkTicket>>(
    MyExpApi.BULK_TICKET_DETAIL_FOR_BTA,
    {
      pathParams: { registrationParticipantId }
    },
    {
      enabled: Boolean(registrationParticipantId),
      onError: () => {
        errorHandler?.();
      },
      onSuccess: data => successHandler?.(data)
    }
  );
}

export function updateBulkTicketDetailsInCache(
  queryClient: QueryClient,
  registrationParticipantId?: string,
  isInvoicePaymentPending?: boolean
) {
  queryClient.setQueryData(getBulkTicketDetailsKey(registrationParticipantId), (tickets?: Array<ApiBulkTicket>) => {
    const invoicePendingTicketIndex =
      tickets?.findIndex(
        ticket =>
          ticket.forwardedToInvoicePayer && ticket.invoicePaymentRequest?.status === InvoicePaymentRequestStatus.PENDING
      ) ?? -1;

    if (invoicePendingTicketIndex !== -1) {
      return (
        tickets?.map(d => {
          if (d.id === tickets?.[invoicePendingTicketIndex].id)
            return { ...d, forwardedToInvoicePayer: isInvoicePaymentPending };
          else return d;
        }) ?? []
      );
    } else return tickets ?? [];
  });
}

export function useRevokeTicket() {
  const copies = useCopies();
  return usePostApiData<void, ApiBulkTicketRevoke>(MyExpApi.REVOKE_BULK_TICKET, {
    onSuccess: () => {
      successToast(copies.get("bulk.ticket.unassigned.successfully"));
    }
  });
}

export function useGetBulkTicketForms({ registrationParticipantId }: { registrationParticipantId: string }) {
  return useGetApiData<Array<ApiBulkTicketEnabledForms>>(
    MyExpApi.GET_INVENTORY_TYPE_FORMS,
    {
      params: { entityId: registrationParticipantId },
      pathParams: { type: InventoryType.BULK_TICKET }
    },
    {
      enabled: Boolean(registrationParticipantId)
    }
  );
}
